"use client"

import Link from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { ChevronLeft, Compass, LayoutDashboard, MessageCircle, ShoppingBag, type LucideIcon } from "lucide-react"
import { useUserRole } from "@/providers/user-role-provider"
import { readClientView } from "@/lib/client-view"
import { stripLocalePrefix, isGlassNavPath, isChromelessPath } from "@/i18n/routing"
import { isRootPath, parentPath } from "@/lib/nav-back"
import { useEffect, useRef, useState } from "react"

// The top-left of the signed-in chrome. Mirrors the bell top-right on every
// breakpoint, with the same visibility rules, so the strip reads as
// [action] — wordmark — bell.
//
// What it is depends on where you are:
//   - Off the dashboard: Dashboard (home for the active mode).
//   - On the dashboard: the one thing that mode reaches for most —
//     Barber → Rewards, Owner → Messages, Client → Explore shops.
//   - Already on that page: Dashboard again, so it is never a link to itself.
//
// BELOW A ROOT IT IS BACK INSTEAD. The shell has no navigation bar, so this was
// the only control in the corner where a back button belongs — and it was a
// shortcut: from a shop page it read "Dashboard" and threw you to the
// dashboard, with no way back to the listings you were browsing. See
// lib/nav-back for which screens count as roots and where Back goes when
// there is no history to pop (a push notification can open a shop page cold).
const HIDE_PREFIXES: ReadonlyArray<string> = ["/auth", "/clients", "/preview"]

function shouldHide(rawPathname: string | null): boolean {
  if (!rawPathname) return false
  const pathname = stripLocalePrefix(rawPathname)
  if (isChromelessPath(pathname)) return true
  if (isGlassNavPath(pathname)) return true
  return HIDE_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

export function TopLeftAction() {
  const pathname = usePathname()
  const router = useRouter()
  const { userId, role, actualUserType, isLoading } = useUserRole()
  // How many times this web view has navigated since it loaded. router.back()
  // is only safe while this is above zero; before that the entry behind us
  // belongs to the shell's launch, not to anything the person did.
  const navigationsRef = useRef(0)
  const firstPathRef = useRef<string | null>(null)
  useEffect(() => {
    if (firstPathRef.current === null) firstPathRef.current = pathname ?? ""
    else if (pathname !== firstPathRef.current) navigationsRef.current += 1
  }, [pathname])
  const [clientView, setClientView] = useState(false)
  useEffect(() => {
    setClientView(readClientView() || (pathname ?? "").startsWith("/dashboard/client"))
  }, [pathname])

  if (isLoading || !userId) return null
  if (shouldHide(pathname)) return null

  const effective = role ?? actualUserType
  const mode: "barber" | "owner" | "client" = clientView ? "client" : effective === "owner" ? "owner" : "barber"
  const onDashboard = (pathname ?? "").startsWith("/dashboard")

  const home = mode === "client" ? "/dashboard/client" : mode === "owner" ? "/dashboard/owner" : "/dashboard/barber"
  const reach =
    mode === "client"
      ? { label: "Explore", href: "/listings", icon: Compass }
      : mode === "owner"
        ? { label: "Messages", href: "/dashboard/owner/messages", icon: MessageCircle }
        : { label: "Rewards", href: "/dashboard/barber/rewards", icon: ShoppingBag }

  const action: { label: string; href: string; icon: LucideIcon } =
    onDashboard && pathname !== reach.href ? reach : { label: "Dashboard", href: home, icon: LayoutDashboard }

  const showBack = !isRootPath(pathname)
  const fallback = parentPath(pathname)

  // An ink pill with the icon in a white disc on its left (top-left-key in
  // globals.css), so the one control in the top-left reads as a button rather
  // than a flat chip on the white pages. Each state uses the icon the floating
  // nav gives the same page.
  const pill =
    "top-left-key inline-flex h-7 md:h-8 items-center gap-1.5 rounded-full pr-2.5 md:pr-3 text-[11px] font-medium text-white border-0 transition-opacity duration-200 hover:opacity-90"
  const disc = "top-left-key-icon grid place-items-center rounded-full bg-white text-booth-ink"

  if (showBack && fallback) {
    return (
      <div className="fixed safe-top-action left-2 md:left-3 lg:left-[1.125rem] z-40">
        <button
          type="button"
          onClick={() => {
            // Pop only what this session actually pushed. Going "back" out of
            // the first page the app opened would leave the web view on
            // whatever preceded it, which is the sign-in route the shell
            // launches on.
            if (navigationsRef.current > 0) router.back()
            else router.push(fallback)
          }}
          aria-label="Back"
          className={pill}
        >
          <span className={disc} aria-hidden>
            <ChevronLeft strokeWidth={2.25} />
          </span>
          Back
        </button>
      </div>
    )
  }

  // The filled black pill (see above) at every breakpoint and in both themes:
  // one control, one look, wherever the chrome sits.
  //
  // On the phone it is a compact 28px pill (h-7 at top-2.5) with tight
  // padding, so it reads as a quiet control next to the bell rather than a
  // slab: its centre sits on the same 24px line as the bell (36px at
  // top-1.5) and the wordmark. The Request-button size is desktop-only.
  //
  return (
    <div className="fixed safe-top-action left-2 md:left-3 lg:left-[1.125rem] z-40">
      <Link href={action.href} className={pill}>
        <span className={disc} aria-hidden>
          <action.icon strokeWidth={2} />
        </span>
        {action.label}
      </Link>
    </div>
  )
}
